import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    TextField,
    useMediaQuery,
    useTheme,
    Drawer,
    Button,
    Stack,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

interface AuditLog {
    id: number;
    user: string;
    action: string;
    table: string;
    timestamp: string;
}

const mockData: AuditLog[] = [
    { id: 1, user: "John Doe", action: "Login", table: "Users", timestamp: "2024-03-31T08:45:00" },
    { id: 2, user: "Jane Smith", action: "Edited Record", table: "Orders", timestamp: "2024-03-30T14:10:00" },
    { id: 3, user: "John Doe", action: "Deleted", table: "Invoices", timestamp: "2024-03-29T10:20:00" },
    { id: 4, user: "Admin", action: "Viewed", table: "Products", timestamp: "2024-03-25T12:30:00" },
];

const AuditLogPanel: React.FC = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [data, setData] = useState<AuditLog[]>([]);
    const [filtered, setFiltered] = useState<AuditLog[]>([]);

    const [userFilter, setUserFilter] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [tableFilter, setTableFilter] = useState("");
    const [dateRange, setDateRange] = useState("This Month");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    useEffect(() => {
        setData(mockData);
    }, []);

    useEffect(() => {
        applyFilters();
    }, [userFilter, actionFilter, tableFilter, dateRange, customStart, customEnd]);

    const applyFilters = () => {
        let result = [...data];

        if (userFilter) result = result.filter((row) => row.user === userFilter);
        if (actionFilter) result = result.filter((row) => row.action === actionFilter);
        if (tableFilter) result = result.filter((row) => row.table === tableFilter);

        if (dateRange !== "Custom") {
            const range = getDateRange(dateRange);
            result = result.filter((row) => {
                const ts = new Date(row.timestamp).getTime();
                return ts >= range.start.getTime() && ts <= range.end.getTime();
            });
        } else if (customStart && customEnd) {
            const start = new Date(customStart + "T00:00:00");
            const end = new Date(customEnd + "T23:59:59");
            result = result.filter((row) => {
                const ts = new Date(row.timestamp).getTime();
                return ts >= start.getTime() && ts <= end.getTime();
            });
        }

        setFiltered(result);
    };

    const getDateRange = (range: string) => {
        const now = new Date();
        const start = new Date(now);
        const end = new Date(now);

        switch (range) {
            case "Today":
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case "Yesterday":
                start.setDate(start.getDate() - 1);
                end.setDate(end.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case "This Week":
                const dayOfWeek = now.getDay(); // 0 = Sunday
                start.setDate(start.getDate() - dayOfWeek);
                start.setHours(0, 0, 0, 0);
                end.setDate(start.getDate() + 6);
                end.setHours(23, 59, 59, 999);
                break;
            case "Last Week":
                start.setDate(start.getDate() - now.getDay() - 7);
                start.setHours(0, 0, 0, 0);
                end.setDate(start.getDate() + 6);
                end.setHours(23, 59, 59, 999);
                break;
            case "Last Month":
                start.setMonth(start.getMonth() - 1, 1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                break;
            default: // "This Month"
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                break;
        }

        return { start, end };
    };

    const getUnique = (key: keyof AuditLog): string[] => {
        const seen: string[] = [];
        data.forEach((item) => {
            const val = String(item[key]);
            if (!seen.includes(val)) seen.push(val);
        });
        return seen;
    };


    const columnDefs: ColDef[] = [
        { field: "user", headerName: "User", flex: 1 },
        { field: "action", headerName: "Action", flex: 1 },
        { field: "table", headerName: "Table", flex: 1 },
        {
            field: "timestamp",
            headerName: "Timestamp",
            flex: 1.5,
            valueFormatter: ({ value }) =>
                new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }).format(new Date(value)),
        },
    ];

    const renderFilters = () => (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} alignItems="center">
            <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>User</InputLabel>
                <Select value={userFilter} label="User" onChange={(e) => setUserFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {getUnique("user").map((u) => (
                        <MenuItem key={u} value={u}>
                            {u}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Action</InputLabel>
                <Select value={actionFilter} label="Action" onChange={(e) => setActionFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {getUnique("action").map((a) => (
                        <MenuItem key={a} value={a}>
                            {a}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Table</InputLabel>
                <Select value={tableFilter} label="Table" onChange={(e) => setTableFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {getUnique("table").map((t) => (
                        <MenuItem key={t} value={t}>
                            {t}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Date Range</InputLabel>
                <Select value={dateRange} label="Date Range" onChange={(e) => setDateRange(e.target.value)}>
                    {["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month", "Custom"].map((r) => (
                        <MenuItem key={r} value={r}>
                            {r}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {dateRange === "Custom" && (
                <>
                    <TextField
                        type="date"
                        label="Start"
                        InputLabelProps={{ shrink: true }}
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                    />
                    <TextField
                        type="date"
                        label="End"
                        InputLabelProps={{ shrink: true }}
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                    />
                </>
            )}
        </Stack>
    );

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Audit Log</Typography>
                {isSmallScreen && (
                    <IconButton onClick={() => setFiltersOpen(true)}>
                        <FilterListIcon />
                    </IconButton>
                )}
            </Box>

            {!isSmallScreen ? (
                renderFilters()
            ) : (
                <Drawer anchor="right" open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                    <Box p={2} width={300}>
                        <Typography variant="h6" mb={2}>
                            Filters
                        </Typography>
                        {renderFilters()}
                        <Button onClick={() => setFiltersOpen(false)} fullWidth variant="contained" sx={{ mt: 2 }}>
                            Apply Filters
                        </Button>
                    </Box>
                </Drawer>
            )}

            <Box className="ag-theme-alpine" sx={{ height: "60vh", width: "100%", borderRadius: 2 }}>
                <AgGridReact rowData={filtered} columnDefs={columnDefs} animateRows domLayout="autoHeight" />
            </Box>
        </Box>
    );
};

export default AuditLogPanel;
